import { useState, useEffect } from "react";
import {
  getAuth,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  MultiFactorInfo,
  MultiFactorResolver,
  RecaptchaVerifier,
  PhoneMultiFactorInfo
} from "firebase/auth";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";

export const useMultiFactorAuth = () => {
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [verificationId, setVerificationId] = useState<string>("");
  const [enrolledFactors, setEnrolledFactors] = useState<MultiFactorInfo[]>([]);
  const [error, setError] = useState<any>(null);

  const showToast = (params: {
    variant: "brand" | "neutral" | "error" | "success";
    title: string;
    description?: string;
    durationMs?: number;
  }) => {
    const { variant, title, description, durationMs = 4000 } = params;
    const options = { description, duration: durationMs };
    if (variant === "success") toast.success(title, options);
    else if (variant === "error") toast.error(title, options);
    else if (variant === "brand") toast.info(title, options);
    else toast(title, options);
  };

  const getRecaptchaVerifier = () => {
    const auth = getAuth();
    const container = document.getElementById("recaptcha-container");

    if (!container) {
      throw new Error("Could not find recaptcha container");
    }

    // Get existing verifier if it exists
    const existingVerifier = (window as any).recaptchaVerifier;
    if (existingVerifier) {
      return existingVerifier;
    }

    // Create new verifier if none exists
    const verifier = new RecaptchaVerifier(
      container,
      {
        size: "invisible"
      },
      auth
    );

    // Store verifier globally
    (window as any).recaptchaVerifier = verifier;

    return verifier;
  };

  const startEnrollment = async (phoneNumber: string) => {
    setIsEnrolling(true);
    setError(null);
    let verifier;

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("No authenticated user");
      }

      verifier = getRecaptchaVerifier();
      const multiFactorSession = await multiFactor(user).getSession();

      const phoneInfoOptions = {
        phoneNumber,
        session: multiFactorSession
      };

      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        verifier
      );

      setVerificationId(verificationId);
      showToast({
        variant: "brand",
        title: "Verification code sent",
        description: `A verification code has been sent to ${phoneNumber}`,
        durationMs: 5000
      });
    } catch (err: any) {
      setError(err);
      throw err;
    } finally {
      setIsEnrolling(false);
    }
  };

  const completeEnrollment = async (verificationCode: string) => {
    setIsEnrolling(true);
    setError(null);

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("No authenticated user");
      }

      const cred = PhoneAuthProvider.credential(
        verificationId,
        verificationCode
      );
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

      await multiFactor(user).enroll(multiFactorAssertion, "Phone Number");

      // Refresh enrolled factors
      const enrolledMfa = multiFactor(user).enrolledFactors;
      setEnrolledFactors(enrolledMfa);
      setVerificationId("");
    } catch (err: any) {
      setError(err);
    } finally {
      setIsEnrolling(false);
    }
  };

  const initiateLoginVerification = async (resolver: MultiFactorResolver) => {
    let verifier;
    try {
      const auth = getAuth();
      verifier = getRecaptchaVerifier();

      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        {
          multiFactorHint: resolver.hints[0],
          session: resolver.session
        },
        verifier
      );

      setVerificationId(verificationId);
      showToast({
        variant: "brand",
        title: "Verification code sent",
        description: `A verification code has been sent to ${
          (resolver.hints[0] as PhoneMultiFactorInfo).phoneNumber
        }`,
        durationMs: 3000
      });
      return verificationId;
    } catch (err: any) {
      Sentry.captureException(err);
      setError(err);
      throw err;
    }
  };

  const verifyLoginCode = async (
    resolver: MultiFactorResolver,
    verificationCode: string
  ) => {
    try {
      const cred = PhoneAuthProvider.credential(
        verificationId,
        verificationCode
      );
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      await resolver.resolveSignIn(multiFactorAssertion);
      return true;
    } catch (err: any) {
      Sentry.captureException(err);
      setError(err);
      throw err;
    }
  };

  const unenrollFactor = async (factorId: string) => {
    setError(null);

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("No authenticated user");
      }

      await multiFactor(user).unenroll(factorId);

      // Refresh enrolled factors
      const enrolledMfa = multiFactor(user).enrolledFactors;
      setEnrolledFactors(enrolledMfa);
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  useEffect(() => {
    const fetchEnrolledFactors = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (user) {
          const mfa = multiFactor(user);
          setEnrolledFactors(mfa.enrolledFactors);
        }
      } catch (err) {
        Sentry.captureException(err);
      }
    };

    fetchEnrolledFactors();
  }, []);

  return {
    isEnrolling,
    error,
    enrolledFactors,
    verificationId,
    startEnrollment,
    completeEnrollment,
    initiateLoginVerification,
    verifyLoginCode,
    unenrollFactor,
    getRecaptchaVerifier
  };
};
